import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
  signal,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HeaderService } from '@shared/services';
import { cloneDeep } from 'lodash-es';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';
import { InvestmentJurisdictionService } from 'src/app/modules/admin/services';
import { ComboboxComponent } from '../combobox/combobox.component';
import { InvestmentJurisdictionQuery } from '@shared/typings/investment-juridisction-query.type';

@Component({
  standalone: false,
  selector: 'gstar-investment-jurisdiction-nav-combobox',
  templateUrl: './investment-jurisdiction-nav-combobox.component.html',
  styleUrls: ['./investment-jurisdiction-nav-combobox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvestmentJurisdictionNavComboboxComponent implements OnInit {
  hasInitialValue = input<boolean>(false);
  isEntityTypeSection = input<boolean>(false);
  filterQuery = input<InvestmentJurisdictionQuery>();

  private readonly investmentJurisdictionService = inject(
    InvestmentJurisdictionService
  );
  private readonly headerService = inject(HeaderService);
  private readonly destroyRef = inject(DestroyRef);

  invJurisdictions: WritableSignal<InvestmentJurisdictionShort[]> = signal<
    InvestmentJurisdictionShort[]
  >([]);

  @Input() value!: InvestmentJurisdictionShort | null | undefined;
  @Input() disabled?: boolean;
  @Input() required?: boolean;

  @Output() selectInvestmentJurisdiction: EventEmitter<
    InvestmentJurisdictionShort | null | undefined
  > = new EventEmitter();
  @Output() clearInvestmentJurisdiction: EventEmitter<void> =
    new EventEmitter();

  @ViewChild('comboBox')
  comboBoxComp!: ComboboxComponent<InvestmentJurisdictionShort>;

  ngOnInit(): void {
    this.headerService.selectedClient$.subscribe(() => {
      if (!this.hasInitialValue()) {
        this.invJurisdictions.set([]);
        this.selectInvestmentJurisdictionHandler(undefined);
      }
      this.loadInvestmentJurisdiction();
    });
    this.investmentJurisdictionService.reloadNavForSidebar$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadInvestmentJurisdiction());
    this.loadInvestmentJurisdiction();
  }

  loadInvestmentJurisdiction(): void {
    const applyItems = (items: InvestmentJurisdictionShort[]): void => {
      const next = cloneDeep(items);
      this.invJurisdictions.set(next);
      this.clearSelectionIfNoLongerAvailable(next);
    };

    if (this.isEntityTypeSection()) {
      this.investmentJurisdictionService
        .getAllInvestmentJurisdiction()
        .subscribe(response => applyItems(response.items));
    } else if (this.filterQuery()) {
      this.investmentJurisdictionService
        .getAllInvestmentJurisdiction(this.filterQuery())
        .subscribe(response => applyItems(response.items));
    } else {
      this.investmentJurisdictionService
        .getInvestmentJurisdictionsForNav()
        .subscribe(response => applyItems(response.items));
    }
  }

  /**
   * When admin hides a jurisdiction from admin
   */
  private clearSelectionIfNoLongerAvailable(
    items: InvestmentJurisdictionShort[]
  ): void {
    const selectedId = this.value?.investmentJurisdictionId;
    if (selectedId === undefined || selectedId === null) {
      return;
    }
    const isStillAvailable = items.some(
      j => j.investmentJurisdictionId === selectedId
    );
    if (!isStillAvailable) {
      this.clearInvestmentJurisdictionHandler();
      queueMicrotask(() => this.comboBoxComp?.clear());
    }
  }

  selectInvestmentJurisdictionHandler(
    investmentJurisdiction?: InvestmentJurisdictionShort | null | undefined
  ): void {
    this.value = investmentJurisdiction;
    this.selectInvestmentJurisdiction.emit(investmentJurisdiction);
  }

  clearInvestmentJurisdictionHandler(): void {
    this.value = null;
    this.selectInvestmentJurisdictionHandler();
    this.clearInvestmentJurisdiction.emit();
  }
}
